import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { forwardRef, useState } from "react";
import Icon from "../../../base/components/Icon";
import buttonDropdownStyles from "../ButtonDropdown/ButtonDropdown.module.scss";
import {
    AppHeaderMenuTrigger,
    type AppHeaderMenuTriggerTone,
} from "../AppHeader/AppHeader";
import { Button } from "../Button";
import { cx } from "../../utils/cx";
import styles from "./StoreStatus.module.scss";

export type StoreStatusTone = AppHeaderMenuTriggerTone;

export type StoreStatusActionVariant = "primary" | "secondary";

export type StoreStatusAction = {
    id: string;
    label: string;
    /** Row style: green Publish vs neutral outlined row. */
    variant: StoreStatusActionVariant;
    /** Optional leading icon (defaults to none). */
    icon?: ReactNode;
    /** Called when the user activates the row; menu closes after. */
    onSelect?: () => void;
    disabled?: boolean;
};

export type StoreStatusProps = Omit<
    HTMLAttributes<HTMLDivElement>,
    "onChange"
> & {
    /** Value line (e.g. “Error”, “OK”). */
    label: ReactNode;
    /** Small grey line above `label`. Default: “Status store”. */
    metaLabel?: ReactNode;
    /** Colors the value line + default status glyph. */
    tone?: StoreStatusTone;
    /** Menu rows (Publish, Purge cache, …). */
    actions: StoreStatusAction[];
    /** Leading visual; defaults to a tone-colored alert glyph in a circular well. */
    icon?: ReactNode;
    /**
     * When true, value line + icon well read as neutral grey (spinner / “Loading”),
     * independent of `tone`.
     */
    loading?: boolean;
    /** `aria-label` on the trigger. */
    "aria-label"?: string;
};

function DefaultStatusGlyph({
    tone,
    muted,
}: {
    tone: StoreStatusTone;
    /** Loading / idle neutral — follows `iconWell` `color` (grey). */
    muted?: boolean;
}) {
    let stroke: string;
    if (muted) {
        stroke = "currentColor";
    } else if (tone === "danger") {
        stroke = "var(--ds-color-text-error)";
    } else if (tone === "warning") {
        stroke = "var(--ds-color-warning-main)";
    } else {
        stroke = "var(--ds-color-text-success)";
    }
    return (
        <svg aria-hidden fill="none" height={18} viewBox="0 0 20 20" width={18}>
            <circle
                cx="10"
                cy="10"
                fill="none"
                r="7"
                stroke={stroke}
                strokeWidth="1.25"
            />
            <path
                d="M10 6.5V11M10 13.25v.01"
                stroke={stroke}
                strokeLinecap="round"
                strokeWidth="1.35"
            />
        </svg>
    );
}

type MenuSecondaryButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    icon?: ReactNode;
};

/**
 * Must forward arbitrary props: Radix `DropdownMenu.Item asChild` merges
 * pointer/keyboard handlers and roving-focus attributes onto this element.
 */
const MenuSecondaryButton = forwardRef<
    HTMLButtonElement,
    MenuSecondaryButtonProps
>(function MenuSecondaryButton(
    { children, icon, disabled, className, type = "button", ...rest },
    ref,
) {
    return (
        <button
            {...rest}
            className={cx(styles.menuActionSecondary, className)}
            disabled={disabled}
            ref={ref}
            type={type}
        >
            {icon ? (
                <span className={styles.menuActionIcon}>{icon}</span>
            ) : null}
            <span className={styles.menuActionLabel}>{children}</span>
        </button>
    );
});

MenuSecondaryButton.displayName = "StoreStatus.MenuSecondaryButton";

/** Preset icons for common status menu rows (Storybook / demos). */
export const storeStatusIcons = {
    get publish() {
        return <Icon icon="push" />;
    },
    get purge() {
        return <Icon icon="purge-cache" />;
    },
    get refresh() {
        return <Icon icon="refresh" />;
    },
} as const;

/**
 * Store status — pill trigger (`AppHeaderMenuTrigger` + `surface="pill"`) and a
 * stacked action panel (primary **Button** + neutral outline rows) matching DS.
 * On viewports ≤767px the pill trigger is **40px** tall; the **meta + label text
 * column is hidden** (icon + chevron only). If you omit `aria-label`, it defaults to
 * `"Store status"` or **`Store status, {label}`** when `label` is a string or number.
 */
export function StoreStatus({
    label,
    metaLabel = "Status store",
    tone = "default",
    actions,
    icon,
    loading = false,
    "aria-label": ariaLabelProp,
    className,
    ...rest
}: StoreStatusProps) {
    const [open, setOpen] = useState(false);

    const ariaLabel =
        ariaLabelProp ??
        (typeof label === "string" || typeof label === "number"
            ? `Store status, ${label}`
            : "Store status");

    const triggerIcon = icon ?? (
        <span
            className={styles.iconWell}
            data-loading={loading ? "" : undefined}
            data-tone={loading ? undefined : tone}
        >
            <DefaultStatusGlyph muted={loading} tone={tone} />
        </span>
    );

    return (
        <div className={cx(styles.root, className)} {...rest}>
            <DropdownMenu.Root onOpenChange={setOpen} open={open}>
                <DropdownMenu.Trigger asChild>
                    <AppHeaderMenuTrigger
                        aria-label={ariaLabel}
                        className={styles.triggerInHeader}
                        compactOnMobile
                        data-loading={loading ? "" : undefined}
                        data-store-status="true"
                        icon={triggerIcon}
                        label={label}
                        metaLabel={metaLabel}
                        surface="pill"
                        tone={tone}
                    />
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        align="end"
                        className={cx(
                            buttonDropdownStyles.content,
                            styles.menuPanel,
                            styles.menuPanelLayout,
                        )}
                        loop
                        onCloseAutoFocus={e => e.preventDefault()}
                        side="bottom"
                        sideOffset={6}
                    >
                        <div className={styles.actions}>
                            {actions.map(action => (
                                <DropdownMenu.Item
                                    asChild
                                    disabled={action.disabled}
                                    key={action.id}
                                    onSelect={() => {
                                        action.onSelect?.();
                                        setOpen(false);
                                    }}
                                >
                                    {action.variant === "primary" ? (
                                        <Button
                                            className={styles.publishButton}
                                            disabled={action.disabled}
                                            fullWidth
                                            iconLeft={action.icon}
                                            size="md"
                                            variant="primary"
                                        >
                                            {action.label}
                                        </Button>
                                    ) : (
                                        <MenuSecondaryButton
                                            disabled={action.disabled}
                                            icon={action.icon}
                                        >
                                            {action.label}
                                        </MenuSecondaryButton>
                                    )}
                                </DropdownMenu.Item>
                            ))}
                        </div>
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        </div>
    );
}

StoreStatus.displayName = "StoreStatus";
